import React, {useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import parse from 'html-react-parser'
import {useIntl} from 'react-intl'
import Helmet from 'react-helmet'

import {
    Box,
    Flex,
    Heading,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    useStyleConfig,
    Divider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useAccessToken, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import {addGCToCart} from '../../hooks/use-gc-add-to-basket'
import {UseUpdateGiftcardInBasket} from '../../hooks/use-update-giftcard-in-basket'
import {useContentAsset} from '../../hooks/use-content-asset'
import {useMiniCartContext} from '../../hooks/use-mini-cart'

import Breadcrumb from '../../components/breadcrumb'
import GiftCertificateForm from '../../components/gift-certificate-form'
import GiftCertificateImage from '../../components/gift-certificate-form/gift-certificate-image'
import EinsteinTaSlotHtml from '../../components/content-slot/einstein-ta-slot-html'
import AmountSelector from './amount-selector'
import CustomAccordionIcon from '../../components/customAccordionIcon'
import pdpAccordionStyles from '../../theme/components/project/pdp-accordion'
import {productCarouselStyles} from '../../theme/components/project/product-carousel'
import {
    GC_ADDED_TO_CART,
    API_ERROR_MESSAGE,
    GC_PAGE_TITLE,
    GC_PAGE_DESCRIPTION
} from '../../constants'

const InformationAccordionItem = ({title, content}) => (
    <AccordionItem mt={8} borderBottom="none" borderColor="#DADADA">
        {({isExpanded}) => (
            <>
                <AccordionButton padding={0} mt={8}>
                    <Heading
                        as="h2"
                        flex="1"
                        textAlign="left"
                        fontWeight="extrabold"
                        fontSize={{base: '2xl', md: 'fontSize32'}}
                        lineHeight={{base: '8', md: '10'}}
                    >
                        {title}
                    </Heading>
                    <CustomAccordionIcon isExpanded={isExpanded} />
                </AccordionButton>
                <AccordionPanel padding={0} mt={6}>
                    <Box sx={pdpAccordionStyles}>{content}</Box>
                </AccordionPanel>
            </>
        )}
    </AccordionItem>
)

const GiftCertificate = () => {
    const {formatMessage} = useIntl()
    const {locale} = useMultiSite()
    const {data: basket} = useCurrentBasket()
    const {getTokenWhenReady} = useAccessToken()
    const config = getConfig()
    const {openMiniCartOnAddToCart} = useMiniCartContext()
    const updateBasket = useShopperBasketsMutation('updateBasket')
    const [amount, setAmount] = useState(null)
    const gcStyles = useStyleConfig('GiftCertificate')
    const showToast = useToast()

    const contentAssets = useContentAsset(
        ['giftcard-description', 'gift-cert-description', 'Shipping-Delays-Returns'],
        locale?.id
    )

    const {giftCardDescription, giftCertDescription, shippingReturns} = useMemo(() => {
        const giftCardHtml = contentAssets?.['giftcard-description']?.body || ''
        const giftCertHtml = contentAssets?.['gift-cert-description']?.body || ''
        const shippingReturnsHtml = contentAssets?.['Shipping-Delays-Returns']?.body || ''

        return {
            giftCardDescription: giftCardHtml && parse(giftCardHtml),
            giftCertDescription: giftCertHtml && parse(giftCertHtml),
            shippingReturns: shippingReturnsHtml && parse(shippingReturnsHtml)
        }
    }, [contentAssets])

    const showSuccess = () => {
        showToast({
            title: formatMessage(GC_ADDED_TO_CART),
            status: 'success',
            duration: 1000
        })
    }

    const showErrorToast = (error) => {
        const isGiftCardLimit = error == 1

        showToast({
            title: isGiftCardLimit
                ? formatMessage(
                      {
                          id: 'gc_limit_exceed.error',
                          defaultMessage:
                              'You can only add up to {giftCardMaxOrderQuantity} GC to your cart.'
                      },
                      {giftCardMaxOrderQuantity: error}
                  )
                : formatMessage(API_ERROR_MESSAGE),
            status: 'error',
            isClosable: !isGiftCardLimit,
            duration: 1000
        })
    }

    const handleSubmitGiftCertificate = async (formData) => {
        const giftCardMaxOrderQuantity = config.app.library.giftCardMaxOrderQuantity
        try {
            const token = await getTokenWhenReady()
            if (!token) return

            const giftCertificateData = {
                amount: Number(amount),
                recipientName: formData.receipientName,
                recipientEmail: formData.receipientEmail,
                shipmentId: 'me',
                senderName: formData.senderName,
                senderEmail: formData.senderEmail
            }

            // Check gift card quantity limit
            if (basket?.giftCertificateItems?.length >= giftCardMaxOrderQuantity) {
                showErrorToast(giftCardMaxOrderQuantity)
                return
            }

            // Add gift certificate to cart
            const giftCertificates = await addGCToCart(token, giftCertificateData, basket)

            const giftCertificateItem = {
                ...giftCertificates.giftCertificateItems[0],
                c_senderEmail: giftCertificateData.senderEmail
            }
            const giftCertificateItemId = giftCertificateItem.giftCertificateItemId

            const giftCertificatesUpdate = await UseUpdateGiftcardInBasket(
                token,
                {
                    ...giftCertificateData,
                    c_senderEmail: giftCertificateData.senderEmail
                },
                basket,
                giftCertificateItemId
            )

            // Calculate and update total amount
            const totalAmount = giftCertificatesUpdate?.giftCertificateItems.reduce(
                (total, item) => total + item.amount,
                0
            )

            // Update basket with gift certificate total
            await updateBasket.mutateAsync({
                parameters: {basketId: basket.basketId},
                body: {
                    c_gcTotal: totalAmount
                }
            })

            showSuccess()
            openMiniCartOnAddToCart()
        } catch (error) {
            showErrorToast(error)
        }
    }

    return (
        <Box bgColor="secondaryPageBgColor" pt={8}>
            <Box
                data-testid="gift-certificate"
                padding={{base: '0 10px', sm: '0 20px', lg: '0 64px'}}
                maxWidth={{base: '100%', lg: '1920px'}}
                margin="0 auto"
            >
                <Breadcrumb />

                <Helmet>
                    <title>{GC_PAGE_TITLE}</title>
                    <meta name="description" content={GC_PAGE_DESCRIPTION} key="description" />
                </Helmet>

                <Flex {...gcStyles.gc}>
                    <Box {...gcStyles.gcInfo}>
                        <GiftCertificateImage />
                    </Box>
                    <Box
                        {...gcStyles.gcForm}
                        sx={{
                            '.giftcert-name': {
                                fontSize: '32px',
                                lineHeight: '40px',
                                fontWeight: 'bold',
                                marginBottom: '2'
                            },
                            '.giftcert-description': {marginBottom: '2'},
                            '.giftcert-states': {marginBottom: '2'}
                        }}
                    >
                        <Box>{giftCardDescription}</Box>
                        <AmountSelector setAmount={(value) => setAmount(Number(value))} />
                        <GiftCertificateForm
                            submitForm={handleSubmitGiftCertificate}
                            amount={amount}
                        />
                    </Box>
                </Flex>

                <Box>
                    <Accordion defaultIndex={[0]} allowMultiple>
                        <InformationAccordionItem
                            title={formatMessage({
                                defaultMessage: 'Description',
                                id: 'product_description.heading.product_description'
                            })}
                            content={giftCertDescription}
                        />
                        <InformationAccordionItem
                            title={formatMessage({
                                defaultMessage: 'Shipping & Returns',
                                id: 'product_description.heading.shipping_returns'
                            })}
                            content={shippingReturns}
                        />
                    </Accordion>
                    <Divider my={8} />
                </Box>
            </Box>

            <Box
                mt={6}
                backgroundImage={`url(${getAssetUrl('static/img/bg_shop_bottom_halftone.png')})`}
                backgroundPosition="0 100%"
                backgroundSize="auto"
                backgroundRepeat="repeat-x"
                sx={productCarouselStyles}
            >
                <Box
                    className="einsteinRecommendations"
                    padding={{base: '0 10px', sm: '0 20px', lg: '0 64px'}}
                    maxWidth={{base: '100%', lg: '1920px'}}
                    margin="0 auto"
                >
                    {[
                        'cts-recommendations-m',
                        'product-recommendations-m',
                        'PDP-recommendations-3-m'
                    ].map((slotId) => (
                        <EinsteinTaSlotHtml
                            key={slotId}
                            slotId={slotId}
                            context="global"
                            type="html"
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

GiftCertificate.getTemplateName = () => 'gift-certificate'

InformationAccordionItem.propTypes = {
    title: PropTypes.string,
    content: PropTypes.any
}
export default GiftCertificate
